// Silent rrweb session recorder — Yamam-only (gated by config.sessionReplay) and only
// for internal non-admin staff (sales/manager/viewer). rrweb is dynamically imported so
// it never loads where recording is off. Events are batched and flushed every few
// seconds + a final keepalive flush on page hide. No UI, no indicator — by design.
import axios from 'axios';

let started = false;
let activeStop = null; // module-level handle so logout/identity-change can tear down (F23)

// Stop the current recording (flush the tail, detach listeners) and allow a fresh,
// identity-bound session to start on the next login. Called from App on logout /
// user change / feature disable. No-op if nothing is recording. (F23)
export function stopSessionRecording() {
  try { activeStop && activeStop(); } catch (e) { /* ignore */ }
  activeStop = null;
  started = false;
}

function newId(user) {
  const rnd = Math.random().toString(36).slice(2, 8);
  const who = String(user?.id || user?.name || 'u').replace(/[^a-zA-Z0-9]/g, '').slice(0, 24);
  return `s_${who}_${Date.now()}_${rnd}`;
}

// A single page-load tab stays open for hours, so one sessionId would span the whole
// day (mostly idle) and read as a bogus "22h" recording. Split into segments: open a
// fresh recording when activity resumes after a long idle gap, and cap any one segment.
const IDLE_MS = 10 * 60 * 1000;      // >10 min with no events ⇒ next activity starts a new segment
const MAX_SEGMENT_MS = 30 * 60 * 1000; // never let one segment run past 30 min of wall-clock

// Start recording for this user. Safe to call repeatedly (guards against double-start).
export function startSessionRecording(user) {
  if (started) return;
  // App.js decides recordability (staff + allow-listed admins). Brokers are a hard
  // never (external partners), guarded here regardless of the caller.
  if (!user || user.role === 'broker') return;
  started = true;

  let sessionId = newId(user);
  let buffer = [];
  let seq = 0;
  let stopFn = null;
  let interval = null;
  let alive = true;
  let rrwebMod = null;
  let lastActivityTs = Date.now();
  let segmentStart = Date.now();
  let idleArmed = false; // went idle; the next tick with fresh activity opens a new segment

  const flush = (final) => {
    if (!buffer.length) return;
    const batch = buffer;
    buffer = [];
    const body = JSON.stringify({ sessionId, seq: seq++, events: batch });
    if (final) {
      // Survives page unload AND can carry the auth header (unlike sendBeacon).
      const token = localStorage.getItem('vroia-token');
      try {
        fetch('/api/replay/ingest', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
          body,
          keepalive: true,
        }).catch(() => {});
      } catch (e) { /* ignore */ }
      return;
    }
    // Normal flush — axios already carries the Authorization header globally.
    axios.post('/api/replay/ingest', JSON.parse(body)).catch((e) => {
      if (e?.response?.status === 404) stop(); // feature off on this tenant → stop quietly
    });
  };

  const onVis = () => { if (document.visibilityState === 'hidden') flush(true); };
  const onHide = () => flush(true);

  function stop() {
    if (!alive) return;
    try { flush(true); } catch (e) {} // persist the tail before tearing down
    alive = false;
    try { stopFn && stopFn(); } catch (e) {}
    if (interval) clearInterval(interval);
    document.removeEventListener('visibilitychange', onVis);
    window.removeEventListener('pagehide', onHide);
    buffer = [];
    if (activeStop === stop) { activeStop = null; started = false; } // allow a new session later
  }
  activeStop = stop; // expose this instance's teardown to stopSessionRecording()

  // (Re)start rrweb recording. Each call emits a fresh full snapshot, so every segment
  // is self-contained and replayable on its own.
  const startRecorder = () => {
    if (!alive || !rrwebMod) return;
    stopFn = rrwebMod.record({
      emit(event) {
        lastActivityTs = Date.now();
        buffer.push(event);
        if (buffer.length >= 200) flush(false); // avoid oversized chunks
      },
      sampling: { mousemove: 50, scroll: 150, media: 800, input: 'last' },
      maskAllInputs: false,
      maskInputOptions: { password: true }, // never capture password fields
      recordCanvas: false,
      collectFonts: false,
    });
    segmentStart = Date.now();
  };

  // Close the current segment and open a new one (new sessionId + fresh snapshot).
  const rotate = () => {
    flush(false);
    try { stopFn && stopFn(); } catch (e) {}
    sessionId = newId(user);
    seq = 0;
    idleArmed = false;
    startRecorder();
  };

  (async () => {
    try {
      rrwebMod = await import('rrweb');
      if (!alive) return;
      startRecorder();
      interval = setInterval(() => {
        if (!alive) return;
        const now = Date.now();
        if (now - lastActivityTs > IDLE_MS) {
          idleArmed = true; // idle now — don't segment empty time, wait for activity to resume
          flush(false);
          return;
        }
        // Activity resumed after a long idle gap, or a segment has run too long → rotate.
        if (idleArmed || now - segmentStart > MAX_SEGMENT_MS) { rotate(); return; }
        flush(false);
      }, 6000);
      document.addEventListener('visibilitychange', onVis);
      window.addEventListener('pagehide', onHide);
    } catch (e) {
      started = false; // rrweb failed to load — give up silently
    }
  })();
}
